import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABEL, MBTI_TEST_URL, VIA_TEST_URL, MEMBER_ROLES, PERSONAL_IDEAL_HELP } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  UserCircle, Save, ExternalLink, Sparkles, HeartHandshake, Loader2,
  Compass, Eye, Gem, Star, Megaphone, Share2, Check, Users2, Award,
  NotebookPen, CalendarDays,
} from "lucide-react";
import { toast } from "sonner";

const emptyProfile = {
  mbti: "", mission: "", vision: "", values: "", personal_ideal: "", value_proposition: "", bio: "",
};

function LinkChip({ href, children }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-xs font-semibold text-brand-blue hover:text-brand-bluehover">
      {children} <ExternalLink size={12} />
    </a>
  );
}

export default function Profile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ ...emptyProfile, ...(user.profile || {}) });
  const [saving, setSaving] = useState(false);
  const [coaches, setCoaches] = useState([]);
  const [sessions, setSessions] = useState([]);
  const isMember = MEMBER_ROLES.includes(user.role);
  const isCoach = user.role === "coach";
  const stats = user.stats || {};

  useEffect(() => {
    if (isMember) {
      api.get("/coaches").then((r) => setCoaches(r.data)).catch(() => {});
      api.get("/my/coaching-sessions").then((r) => setSessions(r.data)).catch(() => {});
    }
  }, [isMember]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profile", form);
      toast.success("Perfil guardado!");
      await refresh();
    } catch (e) {
      toast.error("Erro ao guardar");
    } finally { setSaving(false); }
  };

  const toggleCoach = async (coach) => {
    try {
      if (coach.shared) await api.delete(`/coaches/${coach.id}/share`);
      else await api.post(`/coaches/${coach.id}/share`);
      setCoaches((cs) => cs.map((c) => (c.id === coach.id ? { ...c, shared: !c.shared } : c)));
      toast.success(coach.shared ? "Partilha removida" : `Perfil partilhado com ${coach.name}`);
    } catch (e) {
      toast.error("Erro");
    }
  };

  const fields = [
    { key: "mission", label: "Missão", icon: Compass, ph: "A minha missão como profissional...", link: null },
    { key: "vision", label: "Visão", icon: Eye, ph: "Onde me vejo no futuro...", link: null },
    { key: "values", label: "Valores", icon: Gem, ph: "Os meus valores fundamentais...",
      link: <LinkChip href={VIA_TEST_URL}>Descobrir valores (VIA-ME)</LinkChip> },
    { key: "value_proposition", label: "Proposta de Valor", icon: Megaphone, ph: "O que me distingue enquanto consultor...", link: null },
  ];

  return (
    <div className="space-y-6 max-w-4xl" data-testid="profile-page">
      <div>
        <h1 className="font-heading font-black text-2xl sm:text-3xl flex items-center gap-2">
          <UserCircle className="text-brand-blue" /> O meu perfil
        </h1>
        <p className="text-brand-muted text-sm mt-1">
          {ROLE_LABEL[user.role]} · {user.email}
          {user.sponsor_name && <> · Apadrinhado por <span className="text-brand-orange font-medium">{user.sponsor_name}</span></>}
        </p>
      </div>

      {/* Engagement / sponsorship stats for members */}
      {isMember && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <div className="p-4 rounded-sm bg-brand-surface border border-brand-border">
            <div className="flex items-center gap-2 text-brand-orange"><HeartHandshake size={16} /><span className="text-xs uppercase tracking-wider font-bold">Engagement</span></div>
            <p className="font-heading font-black text-2xl mt-2" data-testid="engagement-points">{stats.engagement_points ?? 0}</p>
            <p className="text-xs text-brand-muted">pontos por apadrinhamento</p>
          </div>
          <div className="p-4 rounded-sm bg-brand-surface border border-brand-border">
            <div className="flex items-center gap-2 text-brand-blue"><Users2 size={16} /><span className="text-xs uppercase tracking-wider font-bold">Apadrinhados</span></div>
            <p className="font-heading font-black text-2xl mt-2">{stats.consultants_sponsored ?? 0}</p>
            <p className="text-xs text-brand-muted">consultores integrados por ti</p>
          </div>
          <div className="p-4 rounded-sm bg-brand-surface border border-brand-border">
            <div className="flex items-center gap-2 text-brand-success"><Award size={16} /><span className="text-xs uppercase tracking-wider font-bold">Nível</span></div>
            <p className="font-heading font-black text-2xl mt-2">{stats.level_name}</p>
            <p className="text-xs text-brand-muted">{stats.points} pontos</p>
          </div>
        </div>
      )}

      {/* Personality & development */}
      <div className="p-6 rounded-sm bg-brand-surface border border-brand-border space-y-5">
        <h2 className="font-heading font-bold text-lg">Personalidade & Desenvolvimento</h2>

        {/* MBTI */}
        <div>
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-2"><Star size={14} className="text-brand-orange" /> Personalidade (MBTI)</Label>
            <LinkChip href={MBTI_TEST_URL}>Fazer teste MBTI</LinkChip>
          </div>
          <Input value={form.mbti} onChange={(e) => set("mbti", e.target.value)} data-testid="profile-mbti"
            placeholder="Ex: ENFJ" className="mt-1.5 bg-background border-brand-border rounded-sm max-w-[200px]" />
        </div>

        {fields.map((f) => (
          <div key={f.key}>
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2"><f.icon size={14} className="text-brand-blue" /> {f.label}</Label>
              {f.link}
            </div>
            <Textarea value={form[f.key]} onChange={(e) => set(f.key, e.target.value)} data-testid={`profile-${f.key}`}
              placeholder={f.ph} rows={2} className="mt-1.5 bg-background border-brand-border rounded-sm" />
          </div>
        ))}

        {/* Personal Ideal (special) */}
        <div className="p-4 rounded-sm bg-brand-blue/5 border border-brand-blue/30">
          <Label className="flex items-center gap-2"><Sparkles size={14} className="text-brand-orange" /> Ideal Pessoal</Label>
          <p className="text-xs text-brand-muted mt-1.5 leading-relaxed">{PERSONAL_IDEAL_HELP}</p>
          <Textarea value={form.personal_ideal} onChange={(e) => set("personal_ideal", e.target.value)} data-testid="profile-personal_ideal"
            placeholder="A ideia predileta que dá sentido ao meu propósito..." rows={2}
            className="mt-2 bg-background border-brand-border rounded-sm" />
        </div>

        <Button onClick={save} disabled={saving} data-testid="save-profile-button"
          className="bg-brand-orange hover:bg-brand-orangehover rounded-sm font-bold">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save size={15} className="mr-1" /> Guardar perfil</>}
        </Button>
      </div>

      {/* Coaching share */}
      {isMember && (
        <div className="p-6 rounded-sm bg-brand-surface border border-brand-border">
          <h2 className="font-heading font-bold text-lg flex items-center gap-2"><HeartHandshake size={18} className="text-brand-orange" /> Acompanhamento por Coach</h2>
          <p className="text-sm text-brand-muted mt-1">Escolhe um ou mais coaches e partilha o teu perfil para receberes acompanhamento personalizado.</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {coaches.map((c) => (
              <div key={c.id} data-testid="coach-card"
                className={`p-4 rounded-sm border flex items-start justify-between gap-3 ${c.shared ? "border-brand-success/40 bg-brand-success/5" : "border-brand-border"}`}>
                <div className="flex items-start gap-3 min-w-0">
                  <div className="h-9 w-9 rounded-sm bg-gradient-to-br from-brand-blue to-brand-orange flex items-center justify-center text-sm font-bold text-white shrink-0">{c.name[0]}</div>
                  <div className="min-w-0">
                    <p className="font-medium truncate">{c.name}</p>
                    <p className="text-xs text-brand-muted line-clamp-2">{c.profile?.bio || c.profile?.mission || "Coach"}</p>
                  </div>
                </div>
                <Button size="sm" onClick={() => toggleCoach(c)} data-testid="toggle-coach-share"
                  className={`rounded-sm font-semibold shrink-0 ${c.shared ? "bg-brand-success/20 text-brand-success hover:bg-brand-success/30" : "bg-brand-blue hover:bg-brand-bluehover"}`}>
                  {c.shared ? <><Check size={14} className="mr-1" /> Partilhado</> : <><Share2 size={14} className="mr-1" /> Partilhar</>}
                </Button>
              </div>
            ))}
            {coaches.length === 0 && <p className="text-sm text-brand-muted">Ainda não há coaches disponíveis.</p>}
          </div>
        </div>
      )}

      {/* Coaching sessions shared with the member */}
      {isMember && sessions.length > 0 && (
        <div className="p-6 rounded-sm bg-brand-surface border border-brand-border" data-testid="my-coaching-sessions">
          <h2 className="font-heading font-bold text-lg flex items-center gap-2"><NotebookPen size={18} className="text-brand-blue" /> Sessões de coaching</h2>
          <p className="text-sm text-brand-muted mt-1">Notas partilhadas pelo teu coach.</p>
          <div className="mt-4 space-y-2">
            {sessions.map((s) => (
              <div key={s.id} className="p-3 rounded-sm border border-brand-border">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium text-sm">{s.topic}</p>
                  <span className="text-xs text-brand-muted flex items-center gap-1"><CalendarDays size={12} /> {new Date(s.date).toLocaleDateString("pt-PT")}</span>
                </div>
                {s.notes && <p className="text-sm text-white/85 mt-1.5">{s.notes}</p>}
                {s.action_items && <p className="text-xs text-brand-orange mt-1">Ações: {s.action_items}</p>}
                <p className="text-xs text-brand-muted mt-1">Coach: {s.coach_name}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Coach's own bio editing hint */}
      {isCoach && (
        <div className="p-4 rounded-sm bg-brand-blue/5 border border-brand-blue/30 text-sm text-brand-muted">
          Como Coach, os consultores que partilharem o perfil aparecem na tua área de <span className="text-white font-medium">Coaching</span>.
        </div>
      )}
    </div>
  );
}
